/**
 * Guard against React crashes caused by in-page translation tools.
 *
 * Chrome's built-in "Translate this page", Google Translate, Immersive Translate
 * and similar tools mutate the DOM outside React's knowledge — they wrap or
 * replace text nodes (e.g. in <font> tags) and move siblings around. When React
 * later commits an update and calls `Node.insertBefore(newNode, ref)` or
 * `Node.removeChild(child)` against a node the translator has re-parented, the
 * browser throws:
 *
 *   "Failed to execute 'insertBefore' on 'Node': The node before which the new
 *    node is to be inserted is not a child of this node."
 *   "Failed to execute 'removeChild' on 'Node': The node to be removed is not a
 *    child of this node."
 *
 * The throw escapes React's commit phase, tears down the whole subtree (white
 * screen) for that user, and shows up as a noisy, non-actionable global error
 * (its stack is pure react-dom, so the extension-frame Sentry filter misses it).
 *
 * This guard makes those two mutations resilient: when the reference/child node
 * is no longer a child of `this`, it warns once and no-ops instead of throwing,
 * so the app survives the translator's interference (React's next render
 * reconciles the tree). Well-formed calls are passed through untouched.
 *
 * Well-known React + browser-translation workaround. Installed once (guarded by a
 * Symbol marker) and only on the client.
 */

const domMutationGuardMarker = Symbol.for('universalx.domMutationGuard');

// Warn at most once per operation so a translated page does not spam the console
// on every React commit.
let warnedRemoveChild = false;
let warnedInsertBefore = false;

if (typeof Node === 'function' && Node.prototype) {
  const prototype = Node.prototype as typeof Node.prototype & Record<PropertyKey, unknown>;

  if (!prototype[domMutationGuardMarker]) {
    const originalRemoveChild = prototype.removeChild;
    prototype.removeChild = function removeChild<T extends Node>(this: Node, child: T): T {
      if (child.parentNode !== this) {
        // External DOM mutation (page translation) detached the node from the
        // parent React still believes owns it. Swallow instead of throwing.
        if (!warnedRemoveChild) {
          warnedRemoveChild = true;
          console.warn(
            '[domMutationGuard] removeChild ignored: node is not a child of the target — likely in-page translation.',
            child,
            this
          );
        }
        return child;
      }
      return originalRemoveChild.call(this, child) as T;
    };

    const originalInsertBefore = prototype.insertBefore;
    prototype.insertBefore = function insertBefore<T extends Node>(
      this: Node,
      newNode: T,
      referenceNode: Node | null
    ): T {
      if (referenceNode && referenceNode.parentNode !== this) {
        if (!warnedInsertBefore) {
          warnedInsertBefore = true;
          console.warn(
            '[domMutationGuard] insertBefore ignored: reference node is not a child of the target — likely in-page translation.',
            referenceNode,
            this
          );
        }
        return newNode;
      }
      return originalInsertBefore.call(this, newNode, referenceNode) as T;
    };

    Object.defineProperty(prototype, domMutationGuardMarker, {
      value: true,
      configurable: true,
    });
  }
}
